import { throttle } from 'lodash';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutationObserverForEditor } from './useMutationObserverForEditor';
import { useResizeObserver } from './useResizeObserver';
import { useFontLoadingObserver } from './useFontLoadingObserver';
import type {
  AnimationType,
  EffectType,
  StartPosition,
} from '../TextParticles.types';

// Properties needed for image generation (useImageLoader)
export type ImageStyleProps = {
  color: string;
  font: string;
  letterSpacing: string;
  textTransform: string;
  boundingBox: DOMRect;
  textAlign: string;
  fontReady: boolean;
};

// Properties needed for effect configuration
export type EffectStyleProps = {
  effectType: EffectType;
  animationType: AnimationType;
  animationDuration: number;
  particleStartSize: number;
  particleEndSize: number;
  particleStartOpacity: number;
  particleEndOpacity: number;
  windDirection: number;
  animationDirection: number;
  speed: number;
  turbulence: number;
  particleColorCount: number;
  particleColor1: string;
  particleColor2: string;
  particleColor3: string;
  particleColor4: string;
  startPosition: StartPosition;
  noise: number;
  amplitude: number;
  presetId: string;
  particleDuration: number;
};

export const useComputedStyle = ({
  compId,
  elementRef,
  isEditor,
  textRef,
}: {
  compId: string;
  elementRef: React.RefObject<HTMLElement | null>;
  isEditor: boolean;
  textRef: React.RefObject<HTMLHeadingElement | null>;
}) => {
  const [imageProps, setImageProps] = useState<ImageStyleProps | undefined>(
    undefined,
  );
  const [effectProps, setEffectProps] = useState<EffectStyleProps | undefined>(
    undefined,
  );

  const previousImagePropsCacheRef = useRef<string>('');
  const previousEffectPropsCacheRef = useRef<string>('');
  const requestedFontLoadsRef = useRef<Set<string>>(new Set());
  const applyComputedStyleRef = useRef<() => void>(() => {});

  const getImagePropsCache = useCallback(
    (props: ImageStyleProps | undefined) => {
      if (!props) {
        return '';
      }
      const boundingBoxData = {
        width: Math.round(props.boundingBox.width),
        height: Math.round(props.boundingBox.height),
      };
      return JSON.stringify({
        ...props,
        boundingBox: boundingBoxData,
        fontReady: props.fontReady,
      });
    },
    [],
  );

  const getEffectPropsCache = useCallback(
    (props: EffectStyleProps | undefined) => {
      if (!props) {
        return '';
      }
      return JSON.stringify(props);
    },
    [],
  );

  const applyComputedStyle = useCallback(() => {
    // assumes styles changed & fonts loaded
    const boundingBox = elementRef.current?.getBoundingClientRect();

    if (
      !elementRef.current ||
      !boundingBox ||
      (boundingBox.width === 0 && boundingBox.height === 0) ||
      !textRef.current
    ) {
      setImageProps(undefined);
      setEffectProps(undefined);
    } else {
      const nextComputedStyle = window.getComputedStyle(elementRef.current);
      const textComputedStyle = window.getComputedStyle(textRef.current);
      const nextTextTransform =
        nextComputedStyle.getPropertyValue('text-transform') ||
        nextComputedStyle.getPropertyValue('--text-transform');
      const nextTextColor =
        nextComputedStyle.getPropertyValue('color') ||
        nextComputedStyle.getPropertyValue('--color');

      const rotationZ = parseFloat(
        nextComputedStyle.getPropertyValue('--comp-rotate-z') || '0deg',
      );
      const rotationAngle = isNaN(rotationZ) ? 0 : rotationZ;

      // getBoundingClientRect returns the transformed bounding box, so we might need to unrotate it
      const adjustedBoundingBox = getUnrotatedBoundingBox(
        boundingBox,
        rotationAngle,
      );

      // Image-related properties
      const nextTextAlign =
        nextComputedStyle.getPropertyValue('text-align') ||
        nextComputedStyle.getPropertyValue('--text-align') ||
        'center';

      // Safari may include line-height in computed `font` (e.g. "700 104px / 124px MyFont"),
      // but CanvasRenderingContext2D.font and FontFaceSet.check/load are unreliable with it.
      const computedFont = textComputedStyle.font || '';
      const canvasFont =
        stripLineHeightFromFontShorthand(computedFont) ||
        computedFont ||
        `${textComputedStyle.fontSize || '16px'} ${
          textComputedStyle.fontFamily || 'sans-serif'
        }`;

      const nextFontReady = canvasFont
        ? document.fonts.check(canvasFont)
        : true;

      // If the font isn't ready yet, explicitly request it and re-compute once it resolves.
      // This is a fallback for browsers where `document.fonts.ready` / `loadingdone` can be flaky.
      if (
        !nextFontReady &&
        canvasFont &&
        typeof (document.fonts as any).load === 'function' &&
        !requestedFontLoadsRef.current.has(canvasFont)
      ) {
        requestedFontLoadsRef.current.add(canvasFont);
        (document.fonts as any).load(canvasFont).then(
          () => {
            // Re-run once the browser reports the font has been loaded.
            applyComputedStyleRef.current();
          },
          () => {},
        );
      }
      const nextImageProps: ImageStyleProps = {
        font: canvasFont,
        letterSpacing: nextComputedStyle.letterSpacing,
        textTransform: nextTextTransform,
        boundingBox: adjustedBoundingBox,
        color: nextTextColor,
        textAlign: nextTextAlign,
        fontReady: nextFontReady,
      };

      // Effect-related properties
      const nextEffectType =
        nextComputedStyle.getPropertyValue('--effect-type');
      const nextAnimationType =
        nextComputedStyle.getPropertyValue('--animation-type');
      const nextAnimationDuration = Number(
        nextComputedStyle.getPropertyValue('--duration'),
      );
      const nextParticleStartSize = Number(
        nextComputedStyle.getPropertyValue('--particle-start-size'),
      );
      const nextParticleEndSize = Number(
        nextComputedStyle.getPropertyValue('--particle-end-size'),
      );
      const nextParticleStartOpacity = Number(
        nextComputedStyle.getPropertyValue('--particle-start-opacity'),
      );
      const nextParticleEndOpacity = Number(
        nextComputedStyle.getPropertyValue('--particle-end-opacity'),
      );
      const nextWindDirection = Number(
        nextComputedStyle.getPropertyValue('--wind-direction'),
      );
      const nextAnimationDirection = Number(
        nextComputedStyle.getPropertyValue('--animation-direction'),
      );
      const nextSpeed = Number(nextComputedStyle.getPropertyValue('--speed'));
      const nextTurbulence = Number(
        nextComputedStyle.getPropertyValue('--turbulence'),
      );
      const nextParticleColorCount = Number(
        nextComputedStyle.getPropertyValue('--particle-color-count'),
      );
      const nextParticleColor1 = nextComputedStyle.getPropertyValue(
        '--text-particles-color-1',
      );
      const nextParticleColor2 = nextComputedStyle.getPropertyValue(
        '--text-particles-color-2',
      );
      const nextParticleColor3 = nextComputedStyle.getPropertyValue(
        '--text-particles-color-3',
      );
      const nextParticleColor4 = nextComputedStyle.getPropertyValue(
        '--text-particles-color-4',
      );
      const nextStartPosition = nextComputedStyle.getPropertyValue(
        '--start-position',
      ) as StartPosition;
      const nextNoise = Number(nextComputedStyle.getPropertyValue('--noise'));
      const nextAmplitude = Number(
        nextComputedStyle.getPropertyValue('--amplitude'),
      );
      const nextPresetId = nextComputedStyle.getPropertyValue('--preset-id');
      const nextParticleDuration = Number(
        nextComputedStyle.getPropertyValue('--particle-duration'),
      );

      const nextEffectProps: EffectStyleProps = {
        effectType: nextEffectType as EffectType,
        animationType: nextAnimationType as AnimationType,
        animationDuration: nextAnimationDuration,
        particleStartSize: nextParticleStartSize,
        particleEndSize: nextParticleEndSize,
        particleStartOpacity: nextParticleStartOpacity,
        particleEndOpacity: nextParticleEndOpacity,
        windDirection: nextWindDirection,
        animationDirection: nextAnimationDirection,
        speed: nextSpeed,
        turbulence: nextTurbulence,
        particleColorCount: nextParticleColorCount,
        particleColor1: nextParticleColor1,
        particleColor2: nextParticleColor2,
        particleColor3: nextParticleColor3,
        particleColor4: nextParticleColor4,
        startPosition: nextStartPosition,
        noise: nextNoise,
        amplitude: nextAmplitude,
        presetId: nextPresetId,
        particleDuration: nextParticleDuration,
      };

      // Update imageProps only if changed
      const previousImagePropsCache = previousImagePropsCacheRef.current;
      const nextImagePropsCache = getImagePropsCache(nextImageProps);

      if (previousImagePropsCache !== nextImagePropsCache) {
        previousImagePropsCacheRef.current = nextImagePropsCache;
        setImageProps(nextImageProps);
      }

      // Update effectProps only if changed
      const previousEffectPropsCache = previousEffectPropsCacheRef.current;
      const nextEffectPropsCache = getEffectPropsCache(nextEffectProps);

      if (previousEffectPropsCache !== nextEffectPropsCache) {
        previousEffectPropsCacheRef.current = nextEffectPropsCache;
        setEffectProps(nextEffectProps);
      }
    }
  }, [elementRef, getImagePropsCache, getEffectPropsCache, textRef]);

  applyComputedStyleRef.current = applyComputedStyle;

  const applyComputedStyleAfterFontsReady = useCallback(() => {
    document.fonts.ready.then(
      () => applyComputedStyleRef.current(),
      () => {},
    );
  }, []);

  const applyComputedStyleAfterFontsReadyThrottled = useMemo(() => {
    return throttle(applyComputedStyleAfterFontsReady, 100, {
      leading: true,
      trailing: true,
    });
  }, [applyComputedStyleAfterFontsReady]);

  useMutationObserverForEditor({
    isEditor,
    compId,
    onStyleChange: () => applyComputedStyleAfterFontsReady(),
  });

  useResizeObserver({
    elementRef,
    onResize: applyComputedStyleAfterFontsReadyThrottled,
  });

  useFontLoadingObserver({
    onFontLoad: applyComputedStyleAfterFontsReady,
  });

  useEffect(() => {
    applyComputedStyleAfterFontsReady();
  }, [applyComputedStyleAfterFontsReady]);

  return { imageProps, effectProps };
};
export type UseComputedStyleResult = ReturnType<typeof useComputedStyle>;

const stripLineHeightFromFontShorthand = (font: string): string => {
  // Remove "/ <line-height>" from the shorthand, keeping the rest intact.
  // Example: "700 104px / 124px benzin, sans-serif" -> "700 104px benzin, sans-serif"
  return (font ?? '').replace(/\s*\/\s*[^ ]+/, '');
};

const getUnrotatedBoundingBox = (
  boundingBox: DOMRect,
  rotationAngle: number,
): DOMRect => {
  // If no rotation or 180-degree rotation, dimensions are not affected
  if (Math.abs(rotationAngle % 180) < 0.1) {
    return boundingBox;
  }

  // For 90 or 270 degree rotations, width and height are swapped
  if (Math.abs((rotationAngle % 180) - 90) < 0.1) {
    return {
      ...boundingBox,
      width: boundingBox.height,
      height: boundingBox.width,
    } as DOMRect;
  }

  const radians = (rotationAngle * Math.PI) / 180;
  const cosAngle = Math.abs(Math.cos(radians));
  const sinAngle = Math.abs(Math.sin(radians));
  const denominator = cosAngle * cosAngle - sinAngle * sinAngle;

  // Near 45° or 135°, use alternative calculation
  if (Math.abs(denominator) < 0.0001) {
    const sum = Math.max(boundingBox.width, boundingBox.height) * Math.sqrt(2);
    return {
      ...boundingBox,
      width: Math.round(sum / 2),
      height: Math.round(sum / 2),
    } as DOMRect;
  }

  const originalWidth =
    (boundingBox.width * cosAngle - boundingBox.height * sinAngle) /
    denominator;
  const originalHeight =
    (boundingBox.height * cosAngle - boundingBox.width * sinAngle) /
    denominator;

  return {
    ...boundingBox,
    width: Math.round(Math.abs(originalWidth)),
    height: Math.round(Math.abs(originalHeight)),
  } as DOMRect;
};
